import {gsap} from "gsap";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {GameAssets} from "../GameAssets";
import {Game} from "../Game";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {GameFeatureMode} from "../enums/Enum";
import {Sound} from "../sound/Sound";

export class CFSOutroView extends PIXI.Container implements StageLayer {
    private _button: ContinueButton;
    private _clickCallback: Function;
    private _countUp: BasicBitmapCountUp;
    private _cfsOutro: PIXI.spine.Spine;
    private _hideCFSOutroTl: gsap.core.Timeline | null;
    private _stopped: boolean;

    constructor() {
        super();
        this.name = "CFSOutroView";
        this.init()
    }

    public show(callback: Function, amount: number, countDuration: number): gsap.core.Timeline {
        this._stopped = false;
        const tl: gsap.core.Timeline = new gsap.core.Timeline(),
            isWinBelowStake: boolean = Game.isWinBelowStake(),
            winNumber: number = Game.model.gameData.accumulatedRoundWin,
            isNoWin: boolean = (winNumber === 0.00);
        this._clickCallback = callback;

        this.visible = true;
        this.alpha = 0;
        tl.add(() => {
            this.onResize();
            Game.keypadPlugin.hide();
            gsap.to([this, this._cfsOutro], {duration: 0.3, alpha: 1});
            this._cfsOutro.state.setAnimation(0, "cfs_outro_loop", true);
            NolimitApplication.addLayer(this.name, this);
        });

        tl.add(() => {
            this._countUp.visible = true;
        }, 0.2);

        if (!(isWinBelowStake || isNoWin)) {
            if (Game.model.bonusFinalWinRatio() < 0.1) {
                countDuration = 0.2;
            }
            tl.add([
                () => {
                    Game.soundPlayer.playSound(Sound.CFS_OUTRO_INITIAL);
                    Game.soundPlayer.stopCurrentMusic();
                    if (countDuration > 0.2) {
                        Game.soundPlayer.playSound(Sound.CFS_OUTRO_COUNT_UP);
                    }
                },
                this._countUp.getCountAnimation(0, amount, countDuration)
            ], 0.2);
            tl.add(() => {
                Game.soundPlayer.playSound(Sound.CFS_OUTRO_COUNT_UP_END);
            });
        } else {
            tl.add(() => {
                this._countUp.stopAt(amount);
            }, 0.2);
        }
        tl.add(() => {
            Game.soundPlayer.switchMusic("CFS_OUTRO");
            Game.stickyView.removeStickyStuffs();
        });
        if (Game.model.gameData.wasFeatureBuy && Game.autoPlayPlugin.isAutoplayRound) {
            tl.to({}, {duration: 1});
            tl.add(() => {
                this.onButtonClick();
            });
        } else {
            tl.add(() => {
                this.showButton();
            });
        }
        return tl;
    }

    public showButton(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        tl.add(() => {
            this._button.enable(true);
            this._button.alpha = 1;
            this._button.visible = true;
        });
        return tl;
    }

    public onResize(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;
        let offset: number = 5;
        if (Game.model.data.numberOfFreespinsPlayed > 9) {
            offset = -5;
        }
        if (NolimitApplication.isLandscape) {
            this._button.position.set(360, 589);
            this._countUp.position.set(360, 360);
        } else {
            const centerBottom = (bounds.bottom - 610) * 0.5 + 610;
            this._button.position.set(360, centerBottom - 80);
            this._countUp.position.set(360, 360);
        }
    }

    private init(): void {
        this._countUp = new BasicBitmapCountUp({
            fontName: GameAssets.BITMAP_FONT_OPENSANS_800,
            fontSize: 100,
        }, 400, new PIXI.Point(0.5, 0.5));

        this._countUp.stopAt(0);
        this._countUp.position.set(360, NolimitApplication.isLandscape ? 260 : 300);
        this._countUp.visible = false;
        this._countUp.alpha = 0.5;

        this.visible = false;
        this._button = new ContinueButton();
        this._button.addClickCallback(() => this.onButtonClick());
        this._button.position.set(360, 615);
        this._button.pivot.set(this._button.width * 0.5, this._button.height * 0.5);
        this._button.enable(false);
        this._button.visible = false;

        this._cfsOutro = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("cfsIntroOutro"));
        this._cfsOutro.position.set(345, 360);
        this._cfsOutro.alpha = 0;

        this.addChild(this._cfsOutro, this._button, this._countUp);
        this.onResize();
    }

    private onButtonClick(): void {
        this._button.enable(false);
        this._hideCFSOutroTl = gsap.timeline();
        this._hideCFSOutroTl.add(() => {
            Game.soundPlayer.playSound(Sound.CONTINUE_BUTTON_CFS_TO_MAIN);
            Game.soundPlayer.switchMusic(GameFeatureMode.NORMAL);
            gsap.to([this._button], {duration: 0.1, alpha: 0});
            this._cfsOutro.state.setAnimation(0, "cfs_outro_out", false);
            this._countUp.visible = false;
        }, 0);
        this._hideCFSOutroTl.add(Game.reelView.hide());
        this._hideCFSOutroTl.add(() => {
            Game.soundPlayer.playSound(Sound.GAME_INTRO_ELEVATOR_SCENE);
        });
        this._hideCFSOutroTl.add(Game.backgroundElements.playLiftAnim(Game.model.data.nextMode as GameFeatureMode));
        this._hideCFSOutroTl.add(() => {
            Game.blackBG.interactive = true;
            Game.blackBG.buttonMode = true;
            Game.blackBG.show(0.001);
            Game.blackBG.once('pointerdown', () => {
                this.stopPostFsOutro();
            });
        }, "<1.7")
        this._hideCFSOutroTl.add(Game.reelView.show());

        this._hideCFSOutroTl.add(() => {
            this._cfsOutro.alpha = 0;
            this.visible = false;
            this._clickCallback();
            Game.keypadPlugin.show();
        });
    }

    private stopPostFsOutro(): void {
        if (this._stopped) {
            return;
        }

        if (this._hideCFSOutroTl && this._hideCFSOutroTl.isActive()) {
            Game.soundPlayer.playSound(Sound.CONTINUE_BUTTON_CFS_TO_MAIN);
            this._hideCFSOutroTl.totalProgress(1);
            Game.soundPlayer.stopSound(Sound.GAME_INTRO_ELEVATOR_SCENE);
            this._hideCFSOutroTl = null;
        }
        Game.blackBG.interactive = false;
        Game.blackBG.buttonMode = false;
        Game.blackBG.hide(0.001);
        //Game.apiPlugin.settings.set(APISetting.USE_SPACE_TO_SPIN, this._prevSpaceBarSettings);
        this._stopped = true;
    }
}